import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';

import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import SensorsIcon from '@mui/icons-material/Sensors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import TableChartIcon from '@mui/icons-material/TableChart';

import {
  setCustomerDashboardModalVisibility,
  setDashboardModalVisibility,
  fetchCustomerDashboardData,
  fetchCustomerLogo,
  setCameFromCustomerDashboard,
  updateCustomerPotentialPipeCoverage,
  updateCustomerBoughtSensors,
} from '../../actions/DashboardActions';
import { selectProject } from '../../actions/ProjectsActions';

import {
  SEASHORE_BLUE,
  ZIMA_BLUE,
  AQUA_MARINE,
  WINTER_GREEN,
  DESERT_FLOWER,
  DARK_GREY,
} from '../DashboardModal/dashboardColors';

import './CustomerDashboardModal.scss';
import { DASHBOARD_GUIDE_TYPES, downloadDashboardGuide } from '../../utils/dashboardGuideDownload';

const formatNumber = (num) => {
  if (num === null || num === undefined || Number.isNaN(num)) return 'N/A';
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatWaterSaved = (liters, unit) => {
  if (liters == null || Number.isNaN(Number(liters))) return null;
  const cubicMeters = Number(liters) / 1000;
  if (unit === 'ft') {
    return { value: formatNumber(cubicMeters * 0.264172), unit: 'kgal' };
  }
  return { value: formatNumber(cubicMeters), unit: 'm³' };
};

const Tile = ({ icon, title, action, children, footnote }) => (
  <div className="kpi-tile">
    <div className="tile-header">
      {icon}
      <span className="tile-title">{title}</span>
      {action ? <span className="tile-action">{action}</span> : null}
    </div>
    <div className="tile-body">{children}</div>
    <div className="tile-footnote">{footnote || ' '}</div>
  </div>
);

Tile.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  action: PropTypes.node,
  children: PropTypes.node,
  footnote: PropTypes.node,
};

const StatPair = ({ value, label, color }) => (
  <div className="stat-pair">
    <span className="stat-value" style={{ color }}>{value}</span>
    <span className="stat-label">{label}</span>
  </div>
);

StatPair.propTypes = {
  value: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  color: PropTypes.string,
};

const CustomerDashboardModal = (props, context) => {
  const dispatch = useDispatch();

  const open = useSelector((state) => state.leaksList.dashboard.customerDashboardModalVisible);
  const customerDashboardData = useSelector((state) => state.leaksList.dashboard.customerDashboardData);
  const customerLogo = useSelector((state) => state.leaksList.dashboard.customerLogo);
  const selectedProject = useSelector((state) => state.leaksList.selectedProject);
  const projects = useSelector((state) => state.leaksList.projectsList?.items || []);
  const projectIndexMap = useSelector((state) => state.leaksList.projectsList?.projectIndexMap || {});
  const customers = useSelector((state) => state.leaksList.customers?.items || []);
  const customersIndexMap = useSelector((state) => state.leaksList.customers?.indexMap || {});
  const user = useSelector((state) => state.leaksList.user);
  const units = useSelector((state) => state.local.units);
  const unitFactor = useSelector((state) => state.local.factor);
  const currentLang = useSelector((state) => state.i18nState?.lang || 'en');
  const userGuides = useSelector((state) => state.leaksList?.optionList?.options?.userGuides);

  const bigUnitConfig = unitFactor?.bigUnitValue?.[units] || { value: 1000, label: 'km' };
  const pipesUnitLabel = bigUnitConfig.label === 'mile' ? 'miles' : bigUnitConfig.label;
  const pipesConversionFactor = bigUnitConfig.value;

  const [visitedProjects, setVisitedProjects] = useState({});
  const [sortField, setSortField] = useState('dashboard_name');
  const [sortDir, setSortDir] = useState('asc');
  const [dragOffset, setDragOffset] = useState(null);
  const [position, setPosition] = useState(null);
  const [waterInfoOpen, setWaterInfoOpen] = useState(false);
  const [pipeSettingsOpen, setPipeSettingsOpen] = useState(false);
  const [sensorsSettingsOpen, setSensorsSettingsOpen] = useState(false);
  const [potentialPipesInput, setPotentialPipesInput] = useState('');
  const [boughtSensorsInput, setBoughtSensorsInput] = useState('');
  const isDragging = useRef(false);
  const dialogRef = useRef(null);

  const selectedProjectItem = projects[projectIndexMap[selectedProject]];
  const customerId = selectedProjectItem?.CustomerID;
  const customer = customerId != null ? customers[customersIndexMap[customerId]] : null;
  const customerName = customer?.CustomerName || selectedProjectItem?.CustomerName || '';

  useEffect(() => {
    if (open && customerId) {
      dispatch(fetchCustomerDashboardData(customerId));
      dispatch(fetchCustomerLogo(customerId));
      setVisitedProjects({});
      setSortField('dashboard_name');
      setSortDir('asc');
      setPosition(null);
    }
  }, [open, customerId, dispatch]);

  const handleDragStart = useCallback((e) => {
    if (e.target.closest('.MuiIconButton-root')) return;
    isDragging.current = true;
    const rect = dialogRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging.current || !dragOffset) return;
      e.preventDefault();
      setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    };
    const handleDragEnd = () => {
      isDragging.current = false;
    };
    if (open) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [open, dragOffset]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sumField = (rows, field) => (rows || []).reduce(
    (acc, row) => acc + (Number(row[field]) || 0),
    0,
  );

  const getDisplayName = (project) => {
    if (project.dashboard_name) return project.dashboard_name;
    const localId = String(project.ID);
    const local = projects[projectIndexMap[localId]]
      || projects.find((p) => p.ShortName === project.ShortName);
    return local?.dashboard_name || project.ShortName || local?.ShortName || '';
  };

  const handleClose = useCallback(() => {
    dispatch(setCustomerDashboardModalVisibility(false));
  }, [dispatch]);

  const handleHelpDownload = useCallback(() => {
    downloadDashboardGuide({
      dashboardType: DASHBOARD_GUIDE_TYPES.CUSTOMER,
      dispatch,
      projectId: selectedProject,
      currentLang,
      expectedGuideName: context.t('customer_dashboard'),
      userGuides,
    });
  }, [dispatch, selectedProject, currentLang, userGuides, context.t]);

  const resolveProjectId = useCallback((dashboardProject) => {
    const directId = String(dashboardProject.ID);
    if (projectIndexMap[directId] != null) return directId;
    const match = projects.find((p) => p.ShortName === dashboardProject.ShortName);
    return match ? String(match.ID) : null;
  }, [projects, projectIndexMap]);

  const handleGoToProject = useCallback((dashboardProject) => {
    const projectId = resolveProjectId(dashboardProject);
    if (!projectId) return;
    setVisitedProjects((prev) => ({ ...prev, [projectId]: true }));
    dispatch(setCustomerDashboardModalVisibility(false));
    dispatch(selectProject(projectId));
    dispatch(setCameFromCustomerDashboard(true));
    dispatch(setDashboardModalVisibility(true));
  }, [dispatch, resolveProjectId]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [open, handleClose]);

  const { data, loading, error } = customerDashboardData;

  const partitioned = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const active = list.filter((p) => !p.is_archived && !p.archived);
    const archived = list.filter((p) => p.is_archived || p.archived);
    return { active, archived };
  }, [data]);

  const sortedActive = useMemo(() => {
    return [...partitioned.active].sort((a, b) => {
      const valA = sortField === 'dashboard_name' ? getDisplayName(a) : (a[sortField] ?? '');
      const valB = sortField === 'dashboard_name' ? getDisplayName(b) : (b[sortField] ?? '');
      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = (valA || 0) - (valB || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [partitioned.active, sortField, sortDir]);

  const totalsAll = useMemo(() => {
    const all = [...partitioned.active, ...partitioned.archived];
    const installed = sumField(all, 'SensorsCount');
    const inactive = sumField(all, 'InactiveSensorsCount');
    // Backend provides per-project: leaksDetected = fixed+located+tbf+repaired,
    // leaksFixed = fixed, leaksTbfWaiting = located+tbf+repaired,
    // leaksTblWaiting = to-be-located.
    const detected = sumField(all, 'leaksDetected');
    const fixed = sumField(all, 'leaksFixed');
    const toBeFixed = sumField(all, 'leaksTbfWaiting');
    const tblWaiting = sumField(all, 'leaksTblWaiting');
    const pipesKm = sumField(all, 'pipes_covered_km');
    const waterLt = sumField(all, 'water_saved_lt');
    const waterPotentialLt = sumField(all, 'water_saved_potential_lt');
    return {
      installed,
      active: installed - inactive,
      inactive,
      detected,
      fixed,
      tblWaiting,
      toBeFixed,
      pipesKm,
      waterLt,
      waterPotentialLt,
    };
  }, [partitioned.active, partitioned.archived]);

  if (!open) return null;

  if (!user.isAQS && !user.dashboard) {
    return null;
  }

  const dialogStyle = position
    ? { left: position.x, top: position.y, transform: 'none' }
    : {};

  // Customer-level inputs (provided by backend when available)
  const customerBoughtSensors = data?.[0]?.sensors_purchased
    ?? customer?.sensors_purchased;
  const customerPotentialPipesKm = data?.[0]?.pipes_potential_km
    ?? customer?.pipes_potential_km;

  const totalCoveredPipes = (totalsAll.pipesKm * 1000) / pipesConversionFactor;
  const totalPotentialPipes = customerPotentialPipesKm != null
    ? (Number(customerPotentialPipesKm) * 1000) / pipesConversionFactor
    : null;

  const waterCalc = formatWaterSaved(totalsAll.waterLt, units);
  const waterPotential = formatWaterSaved(totalsAll.waterPotentialLt, units);

  return (
    <Paper className="customer-dashboard-floating" ref={dialogRef} style={dialogStyle}>
      <div className="customer-dashboard-header" onMouseDown={handleDragStart}>
        <span className="title">{context.t('customer_dashboard')}</span>
        <div className="header-actions">
          <IconButton size="small" onClick={handleHelpDownload} title={context.t('help') || 'Help'}>
            <QuestionMarkIcon />
          </IconButton>
          <IconButton size="small" onClick={handleClose} className="header-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="customer-dashboard-content">
        {loading && (
          <div className="customer-dashboard-loading">
            <CircularProgress />
          </div>
        )}

        {error && (
          <div className="customer-dashboard-error">
            {context.t('error')}: {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="tiles-grid">
              <div className="kpi-tile customer-tile">
                <div className="tile-header">
                  <BusinessIcon />
                  <span className="tile-title customer-name-title">{customerName}</span>
                </div>
                <div className="tile-body customer-tile-body">
                  {customerLogo?.data && (
                    <img className="customer-logo" src={customerLogo.data} alt={customerName || 'customer'} />
                  )}
                </div>
                <div className="tile-footnote">{' '}</div>
              </div>

              <Tile
                icon={<PlumbingIcon />}
                title={context.t('total_pipe_coverage') || context.t('pipe_coverage')}
                action={user?.isAQS ? (
                  <Tooltip title={context.t('potential_pipe_coverage')}>
                    <IconButton size="small" onClick={() => { setPotentialPipesInput(''); setPipeSettingsOpen(true); }}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              >
                <StatPair
                  value={Number.isFinite(totalCoveredPipes) ? totalCoveredPipes.toFixed(1) : 'N/A'}
                  label={`${pipesUnitLabel} ${context.t('calculated')}`}
                  color={SEASHORE_BLUE}
                />
                <StatPair
                  value={totalPotentialPipes != null ? totalPotentialPipes.toFixed(1) : 'N/A'}
                  label={`${pipesUnitLabel} ${context.t('potential')}`}
                  color={SEASHORE_BLUE}
                />
              </Tile>

              <Tile
                icon={<CheckCircleIcon />}
                title={context.t('total_leaks_detected') || context.t('found_leaks')}
              >
                <StatPair
                  value={formatNumber(totalsAll.detected)}
                  label={`# ${context.t('detected')}`}
                  color={WINTER_GREEN}
                />
                <StatPair
                  value={formatNumber(totalsAll.fixed)}
                  label={`# ${context.t('fixed')}`}
                  color={WINTER_GREEN}
                />
              </Tile>

              <Tile
                icon={<WaterDropIcon />}
                title={context.t('total_water_saved') || context.t('water_saved')}
                action={(
                  <Tooltip title={context.t('click_for_methodology_info') || 'Click for calculation methodology'}>
                    <IconButton size="small" onClick={() => setWaterInfoOpen(true)}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: DARK_GREY, lineHeight: 1 }}>?</Typography>
                    </IconButton>
                  </Tooltip>
                )}
                footnote={`*${context.t('based_on_awwa_guidelines')}`}
              >
                <StatPair
                  value={waterCalc ? waterCalc.value : 'N/A'}
                  label={`${waterCalc ? waterCalc.unit : (units === 'ft' ? 'kgal' : 'm³')} ${context.t('calculated')}`}
                  color={SEASHORE_BLUE}
                />
              </Tile>
            </div>

            <div className="tiles-grid-secondary">
              <Tile
                icon={<SensorsIcon />}
                title={context.t('total_sensors')}
                action={user?.isAQS ? (
                  <Tooltip title={context.t('purchased_sensors') || 'Purchased sensors'}>
                    <IconButton size="small" onClick={() => { setBoughtSensorsInput(''); setSensorsSettingsOpen(true); }}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              >
                <StatPair
                  value={customerBoughtSensors != null ? formatNumber(customerBoughtSensors) : 'N/A'}
                  label={`# ${context.t('purchased') || 'Purchased'}`}
                  color={SEASHORE_BLUE}
                />
                <StatPair
                  value={formatNumber(totalsAll.installed)}
                  label={`# ${context.t('installed') || 'Installed'}`}
                  color={AQUA_MARINE}
                />
              </Tile>

              <Tile
                icon={<BuildIcon />}
                title={context.t('maintenance_view')}
              >
                <StatPair
                  value={formatNumber(totalsAll.active)}
                  label={`# ${context.t('active_plural')}`}
                  color={SEASHORE_BLUE}
                />
                <StatPair
                  value={formatNumber(totalsAll.inactive)}
                  label={`# ${context.t('inactive_plural')}`}
                  color={DESERT_FLOWER}
                />
              </Tile>

              <Tile
                icon={<NotificationsActiveIcon />}
                title={context.t('to_be_located_and_to_be_fixed_alerts') || `${context.t('to_be_located')} & ${context.t('to_be_fixed')}`}
                footnote={`*${context.t('to_be_fixed_alerts_footnote') || "'To be Fixed' category includes alerts in statuses 'Located' and 'Update repair details'"}`}
              >
                <StatPair
                  value={formatNumber(totalsAll.tblWaiting)}
                  label={`# ${context.t('to_be_located')}`}
                  color={SEASHORE_BLUE}
                />
                <StatPair
                  value={formatNumber(totalsAll.toBeFixed)}
                  label={`# ${context.t('to_be_fixed')}`}
                  color={WINTER_GREEN}
                />
              </Tile>
            </div>

            <div className="kpi-tile project-details-tile">
              <div className="tile-header">
                <TableChartIcon />
                <span className="tile-title">{context.t('project_details') || 'Project Details'}</span>
              </div>
              <TableContainer className="customer-dashboard-table-container">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '17%' }} sortDirection={sortField === 'dashboard_name' ? sortDir : false}>
                      <TableSortLabel
                        active={sortField === 'dashboard_name'}
                        direction={sortField === 'dashboard_name' ? sortDir : 'asc'}
                        onClick={() => handleSort('dashboard_name')}
                      >
                        {context.t('project_name')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: '10%' }} align="center" sortDirection={sortField === 'SensorsCount' ? sortDir : false}>
                      <TableSortLabel
                        active={sortField === 'SensorsCount'}
                        direction={sortField === 'SensorsCount' ? sortDir : 'asc'}
                        onClick={() => handleSort('SensorsCount')}
                      >
                        {context.t('sensors_installed')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: '9%' }} align="center" sortDirection={sortField === 'InactiveSensorsCount' ? sortDir : false}>
                      <TableSortLabel
                        active={sortField === 'InactiveSensorsCount'}
                        direction={sortField === 'InactiveSensorsCount' ? sortDir : 'asc'}
                        onClick={() => handleSort('InactiveSensorsCount')}
                      >
                        {context.t('inactive_sensors')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: '10%' }} align="center" sortDirection={sortField === 'leaksDetected' ? sortDir : false}>
                      <TableSortLabel
                        active={sortField === 'leaksDetected'}
                        direction={sortField === 'leaksDetected' ? sortDir : 'asc'}
                        onClick={() => handleSort('leaksDetected')}
                      >
                        {context.t('detected_leaks') || context.t('found_leaks')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: '11%' }} align="center" sortDirection={sortField === 'leaksTblWaiting' ? sortDir : false}>
                      <TableSortLabel
                        active={sortField === 'leaksTblWaiting'}
                        direction={sortField === 'leaksTblWaiting' ? sortDir : 'asc'}
                        onClick={() => handleSort('leaksTblWaiting')}
                      >
                        {context.t('to_be_located')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell style={{ width: '12%' }} align="center">
                      {context.t('pipe_coverage')} ({pipesUnitLabel})
                    </TableCell>
                    <TableCell style={{ width: '11%' }} align="center">
                      {context.t('water_saved')} ({units === 'ft' ? 'kgal' : 'm³'})
                    </TableCell>
                    <TableCell style={{ width: '14%' }} align="center">{context.t('action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedActive.map((project) => {
                    const resolvedId = resolveProjectId(project);
                    const isVisited = resolvedId && visitedProjects[resolvedId];
                    const water = formatWaterSaved(project.water_saved_lt, units);
                    return (
                      <TableRow key={resolvedId || getDisplayName(project)} hover>
                        <TableCell>{getDisplayName(project)}</TableCell>
                        <TableCell align="center">{project.SensorsCount ?? '-'}</TableCell>
                        <TableCell align="center">{project.InactiveSensorsCount ?? '-'}</TableCell>
                        <TableCell align="center">{project.leaksDetected ?? '-'}</TableCell>
                        <TableCell align="center">{project.leaksTblWaiting ?? '-'}</TableCell>
                        <TableCell align="center">
                          {project.pipes_covered_km != null
                            ? ((Number(project.pipes_covered_km) * 1000) / pipesConversionFactor).toFixed(1)
                            : '-'}
                        </TableCell>
                        <TableCell align="center">{water ? water.value : '-'}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleGoToProject(project)}
                            disabled={isVisited}
                            className={isVisited ? 'visited-btn' : ''}
                          >
                            {context.t('go_to_project_dashboard') || context.t('go_to_project')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {partitioned.archived.length > 0 && (() => {
                    const detected = sumField(partitioned.archived, 'leaksDetected');
                    const water = formatWaterSaved(sumField(partitioned.archived, 'water_saved_lt'), units);
                    return (
                      <TableRow className="archived-row">
                        <TableCell>
                          {context.t('archived_projects') || context.t('archived')}
                        </TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">{detected}</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">{water ? water.value : '-'}</TableCell>
                        <TableCell align="center">-</TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
                <TableFooter>
                  <TableRow className="totals-row">
                    <TableCell style={{ color: ZIMA_BLUE }}>{context.t('total')}</TableCell>
                    <TableCell align="center" style={{ color: AQUA_MARINE }}>
                      {totalsAll.installed}
                    </TableCell>
                    <TableCell align="center" style={{ color: DESERT_FLOWER }}>
                      {totalsAll.inactive}
                    </TableCell>
                    <TableCell align="center" style={{ color: WINTER_GREEN }}>
                      {totalsAll.detected}
                    </TableCell>
                    <TableCell align="center" style={{ color: SEASHORE_BLUE }}>
                      {totalsAll.tblWaiting}
                    </TableCell>
                    <TableCell align="center" style={{ color: SEASHORE_BLUE }}>
                      {((totalsAll.pipesKm * 1000) / pipesConversionFactor).toFixed(1)}
                    </TableCell>
                    <TableCell align="center" style={{ color: SEASHORE_BLUE }}>
                      {waterCalc ? waterCalc.value : '-'}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
              </TableContainer>
            </div>
          </>
        )}

        {!loading && !error && (!data || data.length === 0) && (
          <div className="customer-dashboard-empty">
            {context.t('no_data')}
          </div>
        )}
      </div>

      <Dialog open={waterInfoOpen} onClose={() => setWaterInfoOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 20000 }}>
        <DialogTitle sx={{ fontWeight: 600, fontFamily: 'inherit', fontSize: '1.5rem' }}>
          {context.t('water_saved_methodology_title')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line', fontFamily: 'inherit', fontSize: '1rem', lineHeight: 1.7 }}>
            {context.t('water_saved_methodology_content')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaterInfoOpen(false)} color="primary">{context.t('close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pipeSettingsOpen} onClose={() => setPipeSettingsOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 20000 }}>
        <DialogTitle sx={{ fontWeight: 600 }}>{context.t('potential_pipe_coverage')}</DialogTitle>
        <DialogContent>
          <input
            type="number"
            value={potentialPipesInput}
            onChange={(e) => setPotentialPipesInput(e.target.value)}
            placeholder={context.t('enter_value')}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', marginTop: '8px' }}
            step="0.1"
            min="0"
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {context.t('potential_pipe_coverage_helper', { unit: pipesUnitLabel })}
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPipeSettingsOpen(false)}>{context.t('cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (customerId && potentialPipesInput !== '') {
                const km = pipesConversionFactor === 1000
                  ? Number(potentialPipesInput)
                  : (Number(potentialPipesInput) * pipesConversionFactor) / 1000;
                dispatch(updateCustomerPotentialPipeCoverage(customerId, km));
              }
              setPipeSettingsOpen(false);
            }}
          >
            {context.t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sensorsSettingsOpen} onClose={() => setSensorsSettingsOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 20000 }}>
        <DialogTitle sx={{ fontWeight: 600 }}>{context.t('purchased_sensors') || 'Purchased sensors'}</DialogTitle>
        <DialogContent>
          <input
            type="number"
            value={boughtSensorsInput}
            onChange={(e) => setBoughtSensorsInput(e.target.value)}
            placeholder={context.t('enter_value')}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', marginTop: '8px' }}
            step="1"
            min="0"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSensorsSettingsOpen(false)}>{context.t('cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (customerId && boughtSensorsInput !== '') {
                dispatch(updateCustomerBoughtSensors(customerId, Number(boughtSensorsInput)));
              }
              setSensorsSettingsOpen(false);
            }}
          >
            {context.t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

CustomerDashboardModal.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default CustomerDashboardModal;
